import React, { useEffect, useState } from 'react';
import { Box, Grid, InputLabel, TextField, Typography, Button, MenuItem } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { detailsSchema } from 'utils/schema';
import {
  addDrChronoChartDetails,
  getDrChronoChartDetails,
  addPaymentStatus,
  getPaymentStatus,
  getAllProviders,
  updatePatientProvider,
} from 'store/slices/patientInfoSlice';
import { getPatientDetailsFromAdmin } from 'store/slices/userSlice';
import { useAppDispatch } from 'store/hooks';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router';
import { ROUTE } from 'route/RouteEnums';
import {
  BillingStatus,
  CarePlans,
  CreditCardStatus,
  InsuranceStatus,
  UNASSIGNED_PROVIDER_ID,
} from 'utils/dropDownList';

const Details = (props: any) => {
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [disableChartId, setDisableChartId] = useState<boolean>(false);
  const [providerList, setProviderList] = useState<any[]>([]);
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(detailsSchema),
    mode: 'all',
  });

  useEffect(() => {
    dispatch(getDrChronoChartDetails(props.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const chartId = response?.data?.chartId === 'Not Available' ? '' : response?.data?.chartId;
          setValue('chartId', chartId, { shouldValidate: false, shouldDirty: false });
          setValue('pricingPlan', response.data.pricingPlan, { shouldValidate: false, shouldDirty: false });
          if (response?.data?.chartId) {
            setDisableChartId(true);
          }
        }
      });

    dispatch(getPaymentStatus(props.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200 && response.data !== null) {
          setValue('creditCardAvailable', response.data.creditCardOnfileStatus, {
            shouldValidate: false,
            shouldDirty: false,
          });
          setValue('creditCardStatus', response.data.creditCardStatus, { shouldValidate: false, shouldDirty: false });
          setValue('billingStatus', response.data.billingStatus, { shouldValidate: false, shouldDirty: false });
          setValue('insCardStatus', response.data.insuranceCardStatus, { shouldValidate: false, shouldDirty: false });
          setValue('coPayAmount', response.data.coPayAmount, { shouldValidate: false, shouldDirty: false });
        }
      });

    dispatch(getAllProviders())
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          // providerId (not userId) is the id space PatientInformation.providerId/Appointments.providerId use
          const providers = (response.data ?? []).filter((p: any) => p.providerId);
          providers.sort((a: any, b: any) =>
            `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
          );
          setProviderList(providers);
        }
      });

    dispatch(getPatientDetailsFromAdmin(props.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200 && response.data) {
          setValue('providerId', response.data.providerId ?? '', { shouldValidate: false, shouldDirty: false });
        }
      });
    //eslint-disable-next-line
  }, []);

  const getPayload = (fromData: any) => {
    return {
      patientId: props.patientId,
      ...fromData,
    };
  };

  const onSubmitForm = (formData: any) => {
    const payload = getPayload(formData);
    dispatch(addDrChronoChartDetails(payload))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          setDisableChartId(true);
          const message = 'Patient details updated successfully.';
          toast(message, {
            type: 'success',
            icon: true,
          });
          reset(formData);
        }
      });

    dispatch(
      addPaymentStatus({
        patientId: props.patientId,
        creditCardOnfileStatus: watch('creditCardAvailable') ? watch('creditCardAvailable') : '',
        creditCardStatus: watch('creditCardStatus') ? watch('creditCardStatus') : '',
        billingStatus: watch('billingStatus') ? watch('billingStatus') : '',
        insuranceCardStatus: watch('insCardStatus') ? watch('insCardStatus') : '',
        coPayAmount: watch('coPayAmount') ? watch('coPayAmount') : '',
      })
    )
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          setDisableChartId(true);
          const message = 'Patient details updated successfully.';
          toast(message, {
            type: 'success',
            icon: true,
          });
          reset(formData);
        }
      });

    if (dirtyFields.providerId) {
      dispatch(updatePatientProvider({ patientId: props.patientId, providerId: formData.providerId }))
        .unwrap()
        .then((response: any) => {
          if (response.status === 200) {
            toast('Primary Provider updated successfully.', {
              type: 'success',
              icon: true,
            });
            reset(formData);
          }
        })
        .catch(() => {
          toast('Unable to update Primary Provider. Please try again.', {
            type: 'error',
            icon: true,
          });
        });
    }
  };

  return (
    <Box component='form' width={'100%'} onSubmit={handleSubmit((formData) => onSubmitForm(formData))}>
      <Grid container spacing={2}>
        <Grid item xs={12} display={'flex'} justifyContent='space-between' alignItems={'center'}>
          <Typography variant='h2' sx={{ fontSize: 20, fontWeight: '500', lineHeight: 1.4 }}>
            Details
          </Typography>
          <Button
            sx={{ fontSize: 14 }}
            variant='text'
            onClick={() =>
              navigate(ROUTE.ADMIN_PATIENT_PROFILE, { state: { patientId: props.patientId, from: 'admin' } })
            }
          >
            VIEW PATIENT PROFILE
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel htmlFor='chartId'>
            Dr Chrono Chart ID<span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <TextField
            fullWidth
            id='email'
            placeholder='CRMA000000'
            autoComplete='chartId'
            {...register('chartId')}
            disabled={disableChartId}
            error={!!errors?.chartId}
            helperText={errors?.chartId ? errors?.chartId?.message : ''}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel htmlFor='pricingPlan'>Care Plan</InputLabel>
          <TextField
            id='pricingPlan'
            select // tell TextField to render select
            fullWidth
            {...register('pricingPlan')}
            value={watch('pricingPlan') ? watch('pricingPlan') : ''}
            error={!!errors?.pricingPlan}
            helperText={errors?.pricingPlan ? errors?.pricingPlan?.message : ''}
          >
            {CarePlans.map((plan) => (
              <MenuItem key={plan.key} value={plan.value} disabled={plan.disabled}>
                {plan.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel htmlFor='providerId'>Primary Provider</InputLabel>
          <TextField
            id='providerId'
            select // tell TextField to render select
            fullWidth
            {...register('providerId')}
            value={watch('providerId') ? watch('providerId') : ''}
            error={!!errors?.providerId}
            helperText={errors?.providerId ? errors?.providerId?.message : ''}
          >
            {/** MM-1136: lets admins clear the primary provider; saves the "default" provider id. */}
            <MenuItem key={UNASSIGNED_PROVIDER_ID} value={UNASSIGNED_PROVIDER_ID}>
              No assigned provider
            </MenuItem>
            {providerList.map((provider) => (
              <MenuItem key={provider.userId} value={provider.providerId}>
                {provider.firstName} {provider.lastName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel htmlFor='creditCardAvailable'>Credit Card On File</InputLabel>
          <TextField
            id='creditCardAvailable'
            select // tell TextField to render select
            fullWidth
            {...register('creditCardAvailable')}
            value={watch('creditCardAvailable') ? watch('creditCardAvailable') : ''}
            error={!!errors?.creditCardAvailable}
            helperText={errors?.creditCardAvailable ? errors?.creditCardAvailable?.message : ''}
          >
            <MenuItem key={1} value='Yes'>
              Yes
            </MenuItem>
            <MenuItem key={2} value='No'>
              No
            </MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel htmlFor='creditCardStatus'>Credit Card Status</InputLabel>
          <TextField
            id='creditCardStatus'
            select // tell TextField to render select
            fullWidth
            {...register('creditCardStatus')}
            value={watch('creditCardStatus') ? watch('creditCardStatus') : ''}
            error={!!errors?.creditCardStatus}
            helperText={errors?.creditCardStatus ? errors?.creditCardStatus?.message : ''}
          >
            {CreditCardStatus.map((status) => (
              <MenuItem key={status.key} value={status.name}>
                {status.value}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel htmlFor='billingStatus'>Billing Status</InputLabel>
          <TextField
            id='billingStatus'
            select // tell TextField to render select
            fullWidth
            {...register('billingStatus')}
            value={watch('billingStatus') ? watch('billingStatus') : ''}
            error={!!errors?.billingStatus}
            helperText={errors?.billingStatus ? errors?.billingStatus?.message : ''}
          >
            {BillingStatus.map((billingStatus) => (
              <MenuItem key={billingStatus.key} value={billingStatus.name}>
                {billingStatus.value}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel htmlFor='insCardStatus'>Payment Type</InputLabel>
          <TextField
            id='insCardStatus'
            select // tell TextField to render select
            fullWidth
            {...register('insCardStatus')}
            value={watch('insCardStatus') ? watch('insCardStatus') : ''}
            error={!!errors?.insCardStatus}
            helperText={errors?.insCardStatus ? errors?.insCardStatus?.message : ''}
          >
            {InsuranceStatus.map((status) => (
              <MenuItem key={status.key} value={status.value}>
                {status.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel htmlFor='coPayAmount'>Insurance Co-Pay Amount</InputLabel>
          <TextField
            id='coPayAmount'
            fullWidth
            {...register('coPayAmount')}
            inputProps={{ maxLength: 6 }}
            value={watch('coPayAmount') ? watch('coPayAmount') : ''}
            error={!!errors?.coPayAmount}
            helperText={errors?.coPayAmount ? errors?.coPayAmount?.message : ''}
          />
        </Grid>
        <Grid item xs={12} display='flex' justifyContent={{ xs: 'center', sm: 'end' }}>
          <Button sx={{ minWidth: { xs: 200, md: 150 } }} disabled={!isDirty} type='submit'>
            SAVE
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Details;
